import { useLocation, useParams } from 'react-router-dom';
import { Stack } from '@locus-taxy/disco-ui';
import { WorkflowActionModal } from '@locus-taxy/encore-grid';
import {
  resolveEntityDisplayValue,
  type RowExpressionContext,
} from '@locus-taxy/encore-ui-toolkit';
import {
  EntityDetailView,
  ruleRowFor,
  useEntityDetailInstance,
  useEntityDetailMetadata,
  type CustomRendererRegistry,
  type CustomSectionSlot,
  type FieldEditDescriptor,
  type RenderCustomApp,
} from '@locus-taxy/entity-detail';
// Grid-adapter subpath (DSCO-5355): bridges `relatedGrid` sections to encore-grid's
// EntityGridViewWrapper and self-registers the related-grid resolve endpoint on import.
import { relatedGridSlot } from '@locus-taxy/entity-detail/grid-adapter';
import { useClientId } from '../../hooks/useClientId';
import DetailsSchemaModal from '../DetailsSchemaModal';
import { useWorkflowActions } from '../../hooks/useWorkflowActions';
import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  useEntityMetadata,
  type EntityMetadataResponse,
} from '../../api/endpoints/useEntityMetadata';
import { useModules, type ModulesConfig } from '../../api/endpoints/useModules';
import { useCurrentUser } from '../../api/endpoints/useCurrentUser';
import {
  useAppRegistry,
  type RemoteAppLookup,
} from '../../api/endpoints/useAppRegistry';
import LoadingSpinner from '../LoadingSpinner';
import {
  buildBreadcrumbsFromModules,
  buildModulesNavigation,
} from '../../utils/modulesNavigation';
import { buildClientPrefixedPath } from '../../utils/url';
import { RoutePath } from '../../types/categories';
import { getApiBaseUrl, GOOGLE_MAPS_API_KEY } from '../../config';
import { getNestedValue } from '../../utils/objectUtils';
import {
  resolveEnumPresentation,
  stripEntityDataPrefix,
  type EnumPresentation,
} from '../../utils/enumPresentation';
import MoreActionsMenu from './MoreActionsMenu';
import EntityStatusChips from './EntityStatusChips';
import EntityAccessDimensionChips from './EntityAccessDimensionChips';
import WorkflowTimelineContainer from './WorkflowTimelineContainer';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { useRouteContext } from '../../hooks/useRouteContext';
import { useDashboardNavigate } from '../../hooks/useDashboardNavigate';
import { useEntityFormAdditionalContext } from '../../hooks/useEntityFormAdditionalContext';
import { isMfeAppConfig } from '../../utils/customApp';
import { resolveEntityTitle } from '../../utils/entityTitle';
import type { CustomAppConfig } from '../../utils/customApp';
import { resolveAppConfig } from '../../utils/resolveAppConfig';
import { CustomDetailsPageContainer } from '../../customApps/CustomDetailsPageContainer';
import { CustomSectionRenderer } from '../../customApps/CustomSectionRenderer';
import { ErrorBoundary } from '../ErrorBoundary';
import { getBugsnagClient } from '../../utils/bugsnag';
import { CustomAppActionModal } from '../CustomAppActionModal';
import type { EntityDetailsAction } from '../../types/actions';

interface EntityDetailsProps {
  entityName?: string;
}

type EntitySchemaInfo = {
  title?: string;
  entityPresentationMetadata?: {
    additionalColumnConfig?: {
      statusFieldPath?: string;
      subStatusFieldPath?: string;
    };
  };
};

type StatusColumns =
  | EntityMetadataResponse['data']['frontend']['gridView']['columns']
  | undefined;

function resolveApiBaseUrl(clientId: string) {
  return clientId ? getApiBaseUrl(clientId) : '';
}

function resolveEntityName(
  overrideName: string | undefined,
  paramName: string | undefined
) {
  return overrideName ?? paramName ?? '';
}

function resolveEntitySchemaInfo(
  entityMetadata: EntityMetadataResponse | null | undefined
) {
  return entityMetadata?.data?.entitySchema as EntitySchemaInfo | undefined;
}

function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * Reads the status value at `path` (else at `fallbackField`) and resolves its
 * configured label and colour against the field it was actually read from.
 * `row` and `context` feed a configured `labelExpression` (DP-7885).
 */
function resolveStatusPresentation(
  entityData: Record<string, unknown>,
  path: string | undefined,
  fallbackField: string,
  columns: StatusColumns,
  row: Record<string, unknown>,
  context: RowExpressionContext
): EnumPresentation | undefined {
  const pathValue = path
    ? asNonEmptyString(getNestedValue(entityData, path))
    : undefined;
  if (pathValue) {
    return resolveEnumPresentation(columns, path, pathValue, row, context);
  }
  return resolveEnumPresentation(
    columns,
    fallbackField,
    asNonEmptyString(entityData?.[fallbackField]),
    row,
    context
  );
}

/**
 * The status path: the entityViewConfig overlay (`frontend`, `entityData.`-
 * prefixed) wins over the raw schema value (DP-3264).
 */
function resolveStatusPath(
  overlayPath: string | undefined,
  schemaPath: string | undefined
): string | undefined {
  return overlayPath ? stripEntityDataPrefix(overlayPath) : schemaPath;
}

/**
 * The entity's list page. On a usecase page (DP-3570) the list stays in the
 * `/uc/{usecase}` namespace so back, breadcrumb AND post-delete navigation all
 * return to the usecase list rather than the default entity list (FR-21).
 * Shared by the breadcrumb trail and the post-delete redirect so the two can
 * never drift.
 */
function resolveEntityListPath(
  entityName: string,
  usecase: string | undefined
): string {
  return usecase
    ? `/entities/${entityName}/uc/${usecase}`
    : `/entities/${entityName}`;
}

/** An entity's declared home app (DP-6873): its nav label and route. */
type AppHome = { label: string; path: string };

/**
 * Where "up" leads for an entity with no module leaf: its home app when one is
 * resolved, else its list page. The ONE place that choice is made — shared by
 * the fallback crumb and the post-delete redirect, the same no-drift contract
 * as {@link resolveEntityListPath}.
 */
function resolveEntityHomePath(
  entityName: string,
  usecase: string | undefined,
  appHome: AppHome | undefined
): string {
  return appHome?.path ?? resolveEntityListPath(entityName, usecase);
}

function resolveHeaderBreadcrumbs(
  moduleCrumbs: Array<{ title: string }>,
  resolvedEntityName: string,
  instanceLabel: string,
  clientId: string,
  usecase: string | undefined,
  entityTitle: string,
  appHome?: AppHome
) {
  // The framework's BreadcrumbItem contract uses `to` (not `href`); a crumb with
  // no `to` renders as the current (non-link) page. The view derives the back
  // affordance from the second-to-last crumb's `to`. On a usecase page (DP-3570)
  // the list-page link stays in the `/uc/{usecase}` namespace so back / breadcrumb
  // navigation returns to the usecase list, not the default entity list (FR-21).
  const listPath = resolveEntityListPath(resolvedEntityName, usecase);
  const crumbs: Array<{ id: string; label: string; to?: string }> =
    moduleCrumbs.map((crumb, i) => ({
      id: `bc-${i}`,
      label: crumb.title,
      to: buildClientPrefixedPath(listPath, clientId),
    }));
  if (crumbs.length === 0) {
    /*
     * No module owns this entity. When the app registry declares a home app
     * for it (DP-6873), that app IS the entity's place in the navigation —
     * the default entity list is a page the user never sees in the nav — so
     * the crumb names and links the app. Otherwise the crumb names the entity
     * itself: its configured display name, else the schema title (both arrive
     * as entityTitle), and only then the raw entityName (DP-7728).
     */
    crumbs.push({
      id: 'fallback',
      label: appHome?.label ?? (entityTitle || resolvedEntityName || ''),
      to: buildClientPrefixedPath(
        resolveEntityHomePath(resolvedEntityName, usecase, appHome),
        clientId
      ),
    });
  }
  return [...crumbs, { id: 'instance', label: instanceLabel }];
}

/**
 * No dashboard-level per-entity body renderers: the tour / map / network-map
 * container surfaces were removed in DSCO-5372 (#452) — tour now renders from
 * TOUR_FIELDS config and map / network-map are MFEs — so the framework derives
 * every body from the detail schema. Kept as an empty registry so re-adding a
 * bespoke renderer later is a one-line change.
 */

/**
 * Build the custom-app (MFE) renderer the framework calls when entity metadata
 * designates a `detailsView.app` (SPEC §5.7 custom-app surfaces). The framework
 * owns no module-federation machinery — it just hands back the config; the
 * dashboard resolves it to MFE coordinates and mounts its remote container.
 */
function buildRenderCustomApp({
  clientId,
  entityName,
  entityTitle,
  rootInstanceKey,
  entityData,
  showTimeline,
  mfeRemountKey,
  entitySchemaInfo,
  usecase,
  remoteAppFor,
  registryReady,
}: {
  clientId: string;
  entityName: string;
  entityTitle: string;
  rootInstanceKey: string | undefined;
  entityData: Record<string, unknown>;
  showTimeline: boolean;
  mfeRemountKey: number;
  entitySchemaInfo: unknown;
  usecase: string | undefined;
  /** DP-4118: `apps` registry alias lookup (`appConfig.remoteApp`). */
  remoteAppFor: RemoteAppLookup;
  /** Hold the MFE mount until the registry lookup has arrived. */
  registryReady: boolean;
}): RenderCustomApp {
  return appConfig => {
    if (!isMfeAppConfig(appConfig)) {
      return null;
    }
    if (!registryReady) {
      return <LoadingSpinner />;
    }
    const resolvedConfig = resolveAppConfig(
      appConfig as CustomAppConfig,
      entityName,
      clientId,
      remoteAppFor(appConfig.appName)
    );
    if (!resolvedConfig) {
      getBugsnagClient()?.notify(
        new Error(
          `Failed to resolve custom app config for entity "${entityName}", appName="${appConfig.appName}"`
        )
      );
      return null;
    }
    return (
      <ErrorBoundary fallback={null}>
        <CustomDetailsPageContainer
          config={resolvedConfig}
          clientId={clientId}
          entityName={entityName}
          entityTitle={entityTitle}
          rootInstanceKey={rootInstanceKey}
          entityData={entityData}
          showTimeline={showTimeline}
          remountKey={mfeRemountKey}
          entitySchemaInfo={entitySchemaInfo}
          usecase={usecase}
        />
      </ErrorBoundary>
    );
  };
}

/**
 * Build the `custom` detail-tab section slot (SPEC's residual per-tab panel —
 * distinct from `buildRenderCustomApp`'s whole-page mechanism, above). Reuses
 * the same MF mount pipeline, plus the section's own parent-scope filter
 * (DP-5128 Decision 1), resolved by `CustomSectionRenderer`.
 */
function buildRenderCustomSection({
  clientId,
  entityTitle,
  entityData,
  rootInstanceKey,
  showTimeline,
  entitySchemaInfo,
  usecase,
  remoteAppFor,
  registryReady,
}: {
  clientId: string;
  entityTitle: string;
  entityData: Record<string, unknown>;
  rootInstanceKey: string | undefined;
  showTimeline: boolean;
  entitySchemaInfo: unknown;
  usecase: string | undefined;
  /** DP-4118: `apps` registry alias lookup (`appConfig.remoteApp`). */
  remoteAppFor: RemoteAppLookup;
  /** Hold the MFE mount until the registry lookup has arrived. */
  registryReady: boolean;
}): CustomSectionSlot {
  return ({ section, entityName, instanceId }) => {
    if (!isMfeAppConfig({ name: section.name, appName: section.name })) {
      return null;
    }
    if (!registryReady) {
      return <LoadingSpinner />;
    }
    return (
      <CustomSectionRenderer
        section={section}
        entityName={entityName}
        instanceId={instanceId}
        clientId={clientId}
        entityTitle={entityTitle}
        entityData={entityData}
        rootInstanceKey={rootInstanceKey}
        showTimeline={showTimeline}
        entitySchemaInfo={entitySchemaInfo}
        usecase={usecase}
        remoteAppFor={remoteAppFor}
      />
    );
  };
}

function findUpdateAction(
  actions: EntityDetailsAction[]
): EntityDetailsAction | undefined {
  return actions.find(action => action.id?.includes('updateEntityAction'));
}

function EntityDetailsContainer({
  entityName: entityNameOverride,
}: Readonly<EntityDetailsProps> = {}) {
  const { entityName: entityNameParam, entityId, usecase } = useParams();
  const clientId = useClientId();
  const navigate = useDashboardNavigate();
  const queryClient = useQueryClient();
  const apiBaseUrl = useMemo(() => resolveApiBaseUrl(clientId), [clientId]);
  const location = useLocation();
  const routeContext = useRouteContext();
  const { additionalContext: entityFormAdditionalContext } =
    useEntityFormAdditionalContext();
  const resolvedEntityName = resolveEntityName(
    entityNameOverride,
    entityNameParam
  );
  const { t } = useTranslation();

  // The framework owns metadata + instance fetching. The dashboard reuses the
  // framework's hooks (same react-query cache, no double-fetch) for the
  // header chrome it still supplies: title, chips, breadcrumbs, and the action
  // list that drives the modal flow.
  const {
    entityData = {},
    actions,
    rootInstanceKey,
    audit,
    refetch: refetchInstance,
  } = useEntityDetailInstance(resolvedEntityName, entityId ?? null);
  /*
   * DP-7885: a status `labelExpression` reads the same row shape as the grid
   * cell and the entity-detail body (`entityData.<field>` beside the record
   * fields), built by the framework's `ruleRowFor`.
   */
  const statusRow = useMemo<Record<string, unknown>>(
    () => ruleRowFor(audit, entityId, entityData),
    [audit, entityId, entityData]
  );
  const { data: me } = useCurrentUser(clientId);
  // `PersonnelEntityData` is an interface, so it lacks the index signature the
  // FEEL context wants; the rule reads it as a plain record either way.
  const statusContext = useMemo<RowExpressionContext>(
    () => ({
      currentUser: me?.data?.entityData as Record<string, unknown> | undefined,
    }),
    [me]
  );
  /*
   * Thread the usecase (DP-3570): this provider supplies the whole details surface — tabbed body,
   * details-MFE config, timeline visibility. Without it the read resolves the entity's DEFAULT
   * document, so the default's details app renders and masks a usecase's own detailsView (AC-9).
   */
  const { timelineVisible } = useEntityDetailMetadata(
    resolvedEntityName,
    usecase
  );

  const { data: entityMetadata } = useEntityMetadata(
    clientId,
    resolvedEntityName,
    { usecase }
  );
  const entitySchemaInfo = resolveEntitySchemaInfo(entityMetadata);
  const frontendMetadata = entityMetadata?.data?.frontend;
  /*
   * The configured display name wins, then the schema title (DP-7728). Absent
   * displayName — no override set, or an older BE — resolves exactly as before.
   * Blank counts as absent: `??` alone would keep an empty name and title the
   * page " Timeline".
   */
  const entityTitle = resolveEntityTitle(
    frontendMetadata?.displayName,
    entitySchemaInfo?.title
  );
  const schemaColumnConfig =
    entitySchemaInfo?.entityPresentationMetadata?.additionalColumnConfig;
  const statusFieldPath = resolveStatusPath(
    frontendMetadata?.statusFieldPath,
    schemaColumnConfig?.statusFieldPath
  );
  const subStatusFieldPath = resolveStatusPath(
    frontendMetadata?.subStatusFieldPath,
    schemaColumnConfig?.subStatusFieldPath
  );
  const statusColumns = frontendMetadata?.gridView?.columns;

  const [schemaModalOpen, setSchemaModalOpen] = useState(false);
  const [mfeRemountKey, setMfeRemountKey] = useState(0);

  const {
    currentWorkflowAction,
    isWorkflowLoading,
    modalVariant,
    handleWorkflowFormSubmit,
    handleWorkflowCloseModal,
    handleWorkflowActionClick,
  } = useWorkflowActions({
    clientId,
    entityName: resolvedEntityName,
    entityId: entityId ?? '',
    refreshData: () => {
      refetchInstance();
      setMfeRemountKey(prev => prev + 1);
    },
  });

  const { data: modulesResponse } = useModules(clientId);
  const { leaves: moduleLeaves } = useMemo(() => {
    const modulesData = modulesResponse?.data as ModulesConfig | undefined;
    const rootItems = modulesData?.modules ?? [];
    return buildModulesNavigation(rootItems, clientId);
  }, [modulesResponse?.data, clientId]);

  const moduleCrumbs = useMemo(
    () => buildBreadcrumbsFromModules(location.pathname, moduleLeaves),
    [location.pathname, moduleLeaves]
  );

  /*
   * Whether a BOTTOM nav item (Approvals, Notifications, Settings) owns this
   * page. Read only to gate the home-app fallback below — the breadcrumb
   * itself keeps reading `modules` alone, so crumbs for bottom-nav entities
   * are unchanged (DP-6873).
   */
  const bottomNavOwned = useMemo(() => {
    const modulesData = modulesResponse?.data as ModulesConfig | undefined;
    const { leaves } = buildModulesNavigation(
      modulesData?.bottomModules ?? [],
      clientId
    );
    return buildBreadcrumbsFromModules(location.pathname, leaves).length > 0;
  }, [modulesResponse?.data, clientId, location.pathname]);

  const {
    remoteAppFor,
    nameFor,
    appIdForEntity,
    isReady: registryReady,
  } = useAppRegistry(clientId);

  /*
   * The entity's home app (DP-6873): the registered app whose metadata
   * declares this entity. Never on a usecase page — FR-21 keeps "up" in the
   * `/uc/{usecase}` list there — and never for an entity a nav item (main or
   * bottom) owns: a nav leaf is a stronger statement of where the list lives
   * than the registry is, and a detail-tab app (approval-decision declares
   * userTask) must not replace it. Gated here once so the crumb and the
   * delete redirect agree.
   */
  const appHome = useMemo<AppHome | undefined>(() => {
    if (usecase || moduleCrumbs.length > 0 || bottomNavOwned) {
      return undefined;
    }
    const appId = appIdForEntity(resolvedEntityName);
    return appId
      ? {
          label: nameFor(appId) ?? appId,
          path: `${RoutePath.APPS}/${appId}`,
        }
      : undefined;
  }, [
    usecase,
    moduleCrumbs,
    bottomNavOwned,
    appIdForEntity,
    nameFor,
    resolvedEntityName,
  ]);

  // DP-5812: code, then name, then id; display only, routes keep the id.
  const instanceLabel = resolveEntityDisplayValue(entityData, entityId);

  const breadcrumbItems = useMemo(
    () =>
      resolveHeaderBreadcrumbs(
        moduleCrumbs,
        resolvedEntityName,
        instanceLabel,
        clientId,
        usecase,
        entityTitle,
        appHome
      ),
    [
      moduleCrumbs,
      resolvedEntityName,
      instanceLabel,
      clientId,
      usecase,
      entityTitle,
      appHome,
    ]
  );

  /*
   * History-aware back (DP-6873): with in-SPA history to return through, the
   * back arrow pops it — landing exactly where the user came from (a custom
   * app, a usecase grid, another detail), state intact. The router stamps
   * `idx` into history.state on every entry it creates; idx 0 (or a non-router
   * entry) is a cold landing — deep link, new tab, a full navigation in —
   * where popping would leave the app, so the crumb-derived link (entity list,
   * or the entity's home app) takes over by leaving `onBack` unset. A refresh
   * keeps history.state, so back after a reload still pops in-app. `idx`
   * rather than `location.key !== 'default'`: a `replace` redirect on a cold
   * landing (client-id correction, the root redirect) mints a fresh key but
   * keeps idx 0, so key-based detection would pop out of the app.
   */
  const canGoBack = (window.history.state?.idx ?? 0) > 0;
  const handleBack = useCallback(() => navigate(-1), [navigate]);

  const status = resolveStatusPresentation(
    entityData,
    statusFieldPath,
    'status',
    statusColumns,
    statusRow,
    statusContext
  );
  const subStatus = resolveStatusPresentation(
    entityData,
    subStatusFieldPath,
    'subStatus',
    statusColumns,
    statusRow,
    statusContext
  );

  const chips = (
    <Stack direction="row" gap={1} align="center">
      <EntityAccessDimensionChips entityData={entityData} />
      <EntityStatusChips
        status={status?.label}
        statusColor={status?.color}
        subStatus={subStatus?.label}
        subStatusColor={subStatus?.color}
      />
    </Stack>
  );

  const moreMenu = (
    <MoreActionsMenu onViewSchema={() => setSchemaModalOpen(true)} t={t} />
  );

  // The framework now owns the workflow actions end-to-end — it renders the
  // Actions dropdown from instance.actions AND opens its own action modal
  // (encore-form) on select. The dashboard only supplies the form's context +
  // map key; no onAction wiring needed.

  // Inline-edit commit and non-inline (collection / rich-widget) edits both
  // route to the existing update action — inline commits invoke it directly,
  // modal-routed fields open the established WorkflowActionModal. The modal
  // path keeps using EncoreFormRenderer (unchanged by this framework).
  /*
   * DP-4979 — the framework reports a completed action; the app decides where
   * it leads. A delete leaves the user on the detail page of a record that no
   * longer exists, so send them back to the list.
   *
   * This replaces the redirect that used to live in useWorkflowActions: the
   * dropdown moved into the framework in DP-3091 and that branch became
   * unreachable, since the hook is now only ever handed an update action.
   */
  const handleActionSuccess = useCallback(
    (action: { id?: string }) => {
      if (!action?.id?.includes('deleteEntityAction')) {
        return;
      }
      /*
       * The grid keeps its own cache (encore-grid useGridEntitiesFetch, 30s
       * staleTime) under two key prefixes — 'gridData' for a single page and
       * 'gridDataInfinite' for the infinite list. Without this the destination
       * can render the row we just deleted. Prefix-matched on purpose: the rest
       * of the key carries filters, projection and endpoint, none of which we
       * know or care about here.
       */
      queryClient.invalidateQueries({
        predicate: query =>
          typeof query.queryKey[0] === 'string' &&
          query.queryKey[0].startsWith('gridData') &&
          query.queryKey[1] === clientId &&
          query.queryKey[2] === resolvedEntityName,
      });
      /*
       * Same destination rule as the list crumb (DP-6873): an entity whose
       * only home in the nav is a custom app returns there after a delete; a
       * module-owned entity returns to its list as before.
       */
      navigate(
        buildClientPrefixedPath(
          resolveEntityHomePath(resolvedEntityName, usecase, appHome),
          clientId
        )
      );
    },
    [queryClient, navigate, clientId, resolvedEntityName, usecase, appHome]
  );

  const handleEditViaModal = useCallback(
    (_descriptor: FieldEditDescriptor) => {
      const updateAction = findUpdateAction(actions as EntityDetailsAction[]);
      if (updateAction) {
        handleWorkflowActionClick(updateAction);
      }
    },
    [actions, handleWorkflowActionClick]
  );

  const handleCommit = useCallback(
    async (next: Record<string, unknown>) => {
      const updateAction = findUpdateAction(actions as EntityDetailsAction[]);
      if (!updateAction) {
        return;
      }
      handleWorkflowActionClick(updateAction);
      await Promise.resolve(handleWorkflowFormSubmit(next));
    },
    [actions, handleWorkflowActionClick, handleWorkflowFormSubmit]
  );

  // rootInstanceKey comes from the framework hook (instance.metadata.rootInstanceKey);
  // it lives beside entityData, not inside it.

  const customRenderers = useMemo<CustomRendererRegistry>(() => ({}), []);

  const renderCustomApp = useMemo(
    () =>
      buildRenderCustomApp({
        clientId,
        entityName: resolvedEntityName,
        entityTitle,
        rootInstanceKey,
        entityData,
        showTimeline: timelineVisible,
        mfeRemountKey,
        entitySchemaInfo,
        usecase,
        remoteAppFor,
        registryReady,
      }),
    [
      clientId,
      resolvedEntityName,
      entityTitle,
      rootInstanceKey,
      entityData,
      timelineVisible,
      mfeRemountKey,
      entitySchemaInfo,
      usecase,
      remoteAppFor,
      registryReady,
    ]
  );

  const renderCustomSection = useMemo(
    () =>
      buildRenderCustomSection({
        clientId,
        entityTitle,
        entityData,
        rootInstanceKey,
        showTimeline: timelineVisible,
        entitySchemaInfo,
        usecase,
        remoteAppFor,
        registryReady,
      }),
    [
      clientId,
      entityTitle,
      entityData,
      rootInstanceKey,
      timelineVisible,
      entitySchemaInfo,
      usecase,
      remoteAppFor,
      registryReady,
    ]
  );

  const timelineSlot = useCallback<
    (args: { aggregations: Record<string, unknown> | undefined }) => ReactNode
  >(
    () =>
      rootInstanceKey ? (
        <WorkflowTimelineContainer
          clientId={clientId}
          entityName={resolvedEntityName}
          rootInstanceKey={rootInstanceKey}
          title={`${entityTitle} Timeline`}
        />
      ) : null,
    [clientId, resolvedEntityName, rootInstanceKey, entityTitle]
  );

  const workflowRouteContext = useMemo(
    () => ({
      ...routeContext,
      entity: `${routeContext.entity}-${currentWorkflowAction?.label ?? currentWorkflowAction?.id}`,
    }),
    [routeContext, currentWorkflowAction]
  );

  return (
    <Stack className="h-full overflow-hidden">
      <EntityDetailView
        entityName={resolvedEntityName}
        instanceId={entityId ?? ''}
        title={instanceLabel || undefined}
        breadcrumbItems={breadcrumbItems}
        onBack={canGoBack ? handleBack : undefined}
        chips={chips}
        actionFormContext={
          entityFormAdditionalContext as Record<string, unknown>
        }
        googleApiKey={GOOGLE_MAPS_API_KEY}
        moreMenu={moreMenu}
        customRenderers={customRenderers}
        renderCustomApp={renderCustomApp}
        relatedGridSlot={relatedGridSlot}
        renderCustomSection={renderCustomSection}
        timelineSlot={timelineSlot}
        onCommit={handleCommit}
        onEditViaModal={handleEditViaModal}
        onActionSuccess={handleActionSuccess}
      />

      {currentWorkflowAction?.customApp &&
      isMfeAppConfig(currentWorkflowAction.customApp) ? (
        <CustomAppActionModal
          open={!!currentWorkflowAction}
          onClose={handleWorkflowCloseModal}
          customApp={currentWorkflowAction.customApp}
          title={currentWorkflowAction.label}
          clientId={clientId}
          entityName={resolvedEntityName}
          entityData={entityData}
        />
      ) : (
        <WorkflowActionModal
          entityData={entityData}
          open={!!currentWorkflowAction}
          onClose={handleWorkflowCloseModal}
          form={currentWorkflowAction?.form?.schema}
          onSubmit={handleWorkflowFormSubmit}
          isLoading={isWorkflowLoading}
          title={currentWorkflowAction?.label}
          entityName={resolvedEntityName}
          actionType="single"
          clientId={clientId}
          apiBaseUrl={apiBaseUrl}
          googleApiKey={GOOGLE_MAPS_API_KEY}
          routeContext={workflowRouteContext}
          additionalContext={entityFormAdditionalContext}
          {...modalVariant}
        />
      )}

      <DetailsSchemaModal
        open={schemaModalOpen}
        onClose={() => setSchemaModalOpen(false)}
        entityMetadata={entityMetadata}
        entityName={entityTitle}
        actions={actions as EntityDetailsAction[]}
        entityData={{ actions, data: { entityData } }}
      />
    </Stack>
  );
}

export default function EntityDetails(
  props: Readonly<EntityDetailsProps> = {}
) {
  return <EntityDetailsContainer {...props} />;
}
